// The reservations of a tournament session, as Add Round → Tournament Match
// offers them. Scores are entered by reservation: tick one — or several on the
// same course, which then share one round — and save that one round. A
// reservation is entered at most once. The backend lays a session out
// (GET /api/tournaments/:id/round-groups/:n → tee_times, unassigned_groups).

export interface SessionPlayer {
  player_id: number;
  first_name: string;
  last_name: string;
}

export interface SessionTeeTime {
  reservation_id: number;
  tee_time?: string | null;
  course_id?: number | null;
  course_name?: string | null;
  course_key: string;
  players: SessionPlayer[];
  round_id?: number | null;
  entered?: boolean;
}

export interface UnassignedGroup {
  group_id: number;
  group_number: number;
  course_id?: number | null;
  course_name?: string | null;
  course_key: string;
  players: SessionPlayer[];
  entered?: boolean;
}

// A reservation to enter — or a group the schedule gave no tee time.
export interface SessionReservation {
  key: string;
  reservation_id: number | null;
  group_number: number | null;
  tee_time: string | null;
  course_id: number | null;
  course_name: string | null;
  course_key: string;
  players: SessionPlayer[];
  entered: boolean;
}

// Every reservation someone plays on (entered or not), then tee-time-less groups.
export function sessionReservations(teeTimes: SessionTeeTime[] = [], groups: UnassignedGroup[] = []): SessionReservation[] {
  return [
    ...teeTimes
      .filter((t) => t.entered || t.round_id != null || t.players.length > 0)
      .map((t) => ({
        key: `r${t.reservation_id}`,
        reservation_id: t.reservation_id,
        group_number: null,
        tee_time: t.tee_time || null,
        course_id: t.course_id ?? null,
        course_name: t.course_name || null,
        course_key: t.course_key,
        players: t.players,
        entered: !!t.entered || t.round_id != null,
      })),
    ...groups.map((g) => ({
      key: `g${g.group_id}`,
      reservation_id: null,
      group_number: g.group_number,
      tee_time: null,
      course_id: g.course_id ?? null,
      course_name: g.course_name || null,
      course_key: g.course_key,
      players: g.players,
      entered: !!g.entered,
    })),
  ];
}

// Can still be entered.
export const isOpen = (r: SessionReservation) => !r.entered && r.players.length > 0;

// Reservations share a round only on the same course — a round has one course.
// One whose course isn't known goes with any.
export function sameCourse(a: SessionReservation, b: SessionReservation): boolean {
  return a.course_key === 'none' || b.course_key === 'none' || a.course_key === b.course_key;
}

export const canAdd = (r: SessionReservation, selected: SessionReservation[]) =>
  selected.every((s) => sameCourse(s, r));

// What to tick when a session opens: its only reservation left to enter, if
// there's just one; otherwise the organiser picks.
export function defaultSelection(list: SessionReservation[]): string[] {
  const open = list.filter(isOpen);
  return open.length === 1 ? [open[0].key] : [];
}

// The course a selection is played on: the first that names one by id, else by
// name only (not a known course — the organiser picks it).
export function selectionCourse(selected: SessionReservation[]): { course_id: number | null; course_name: string | null } {
  const withId = selected.find((r) => r.course_id != null);
  if (withId) return { course_id: withId.course_id, course_name: withId.course_name };
  const named = selected.find((r) => r.course_name);
  return { course_id: null, course_name: named ? named.course_name : null };
}

// The players of a selection, in order, each once.
export function selectionPlayers(selected: SessionReservation[]): SessionPlayer[] {
  const seen = new Set<number>();
  const out: SessionPlayer[] = [];
  for (const r of selected) {
    for (const p of r.players) {
      if (seen.has(p.player_id)) continue;
      seen.add(p.player_id);
      out.push(p);
    }
  }
  return out;
}

// The score-entry rows for a set of players, keeping whatever was already typed
// for anyone still listed, plus players added by hand (rows with no display
// name). At least two rows, as elsewhere.
export function rowsForPlayers<T extends { player_id: number | null; display_name?: string }>(
  players: SessionPlayer[],
  current: T[],
  makeRow: (p: SessionPlayer | null) => T,
): T[] {
  const existing = new Map<number, T>();
  for (const row of current) if (row.player_id != null) existing.set(row.player_id, row);
  const seen = new Set<number>();
  const rows: T[] = [];
  for (const p of players) {
    if (seen.has(p.player_id)) continue;
    seen.add(p.player_id);
    const kept = existing.get(p.player_id);
    const fresh = makeRow(p);
    rows.push(kept ? { ...kept, display_name: fresh.display_name } : fresh);
  }
  for (const row of current) {
    if (row.display_name == null && row.player_id != null && !seen.has(row.player_id)) {
      seen.add(row.player_id);
      rows.push(row);
    }
  }
  while (rows.length < 2) rows.push(makeRow(null));
  return rows;
}
